/**
 * <product-gallery> — miniaturas-ancla + pista de frames.
 *
 * Desktop (≥990px): los frames forman un grid editorial de 2 columnas; cada
 * miniatura es un ancla que hace scroll suave de la página hasta su frame (la
 * activa es la última clicada — no hay "imagen actual" con todas visibles a
 * la vez, así que un scrollspy sería ambiguo).
 *
 * Mobile (<990px), y también en escritorio en los modos `carousel` y `duo`: la pista de
 * frames es un carrusel horizontal de CSS NATIVO (`overflow-x: auto` + `scroll-snap`): el
 * navegador hace el gesto, la inercia, el encaje, la rueda horizontal y el teclado, y este
 * módulo lo recorre con la aritmética de `scrollLeft` que hay más abajo. Hasta el 2026-10
 * Embla tomaba la pista con ratón en escritorio; se quitó para que el riel sea el mismo
 * con dedo y con ratón. Embla sigue solo en el visor de zoom (`#openLightbox`).
 *
 * El bucle de las flechas envuelve en los dos sentidos: «siguiente» en la última
 * foto vuelve a la primera, y «anterior» en la primera salta a la última. Las dos
 * flechas se quedan a la vista. El gesto también da la vuelta: `bucleNativo` clona el
 * juego de fotos a cada lado de la pista cuando desborda.
 *
 * El cambio de variante (<product-page>) navega a su imagen en todos los modos.
 */

import { prefersReduced } from "motion/tokens";
import { cederEjeVertical } from "core/wheel-axis";
import { lockScroll, unlockScroll } from "core/scroll-lock";
import {
  cargarEmbla,
  montar,
  instante,
  alVer,
  arrastrandoCarrusel,
  bucleNativo,
} from "core/embla";
/** Mismos glifos que las flechas de subcategorías de la PLP (`icon-arrow-*`). */
const LIGHTBOX_ARROW_PREV = `<span class="icon icon-arrow-left" aria-hidden="true"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="none"><path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="var(--icon-stroke-width)" d="M12.5 5L7.5 10L12.5 15"/></svg></span>`;
const LIGHTBOX_ARROW_NEXT = `<span class="icon icon-arrow-right" aria-hidden="true"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="none"><path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="var(--icon-stroke-width)" d="M7.5 5L12.5 10L7.5 15"/></svg></span>`;
const LIGHTBOX_CLOSE = `<svg aria-hidden="true" viewBox="0 0 24 24" width="24" height="24" fill="none"><path d="M6 6l12 12M18 6L6 18" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>`;
/** Salto de los botones ± (100% → 200% → 300%). Con los dedos el zoom es CONTINUO (pinza). */
const ZOOM_STEP = 1;
const ZOOM_MAX = 3;
/** Zoom del doble toque. Dos dedos llegan a donde quieran; el atajo aterriza aquí. */
const ZOOM_DOBLE = 2;
/**
 * Por encima de esto la foto se panea y Embla suelta el riel. Es 1,01 y no 1:
 * con la pinza el zoom pasa por todos los valores intermedios, y a 1,2 la foto ya
 * desborda el pozo —hay que poder moverla, y el swipe entre fotos ya no toca—.
 * Con los botones el primer paso salta a 2.
 */
const ZOOM_PAN = 1.01;
/** Toque = pulsación breve y quieta. Más allá de esto es un arrastre. */
const TOQUE_MS = 250;
const TOQUE_PX = 12;
/** Doble toque: dos toques seguidos, cerca el uno del otro. */
const DOBLE_MS = 320;
const DOBLE_PX = 40;

const DESKTOP = window.matchMedia("(min-width: 990px)");

class ProductGallery extends HTMLElement {
  /** @type {(() => void) | null} */
  #onModeChange = null;

  /** @type {{ loadAndOpen: (i: number) => void, destroy?: () => void } | null} */
  #lightbox = null;
  #lightboxLoading = false;
  /** @type {number | null} Índice pedido mientras el módulo aún carga. */
  #pendingOpen = null;
  /** @type {HTMLElement | null} */
  #lightboxRoot = null;
  /** @type {any} */
  #lightboxEmbla = null;
  #lightboxZoom = 1;
  /** @type {Element | null} */
  #lightboxOpener = null;
  /** @type {((event: KeyboardEvent) => void) | null} */
  #lightboxOnKey = null;
  /** @type {HTMLElement | null} */
  #lightboxZoomValue = null;
  /** @type {HTMLButtonElement | null} */
  #lightboxZoomOut = null;
  /** @type {HTMLButtonElement | null} */
  #lightboxZoomIn = null;
  /** @type {{ x: number, y: number } | null} */
  #lightboxPointer = null;
  /** Desplazamiento de la foto ampliada, en px de pantalla. */
  #lightboxPan = { x: 0, y: 0 };
  /** El último gesto vino de un dedo: el ratón panea siguiendo al cursor, el dedo arrastra. */
  #lightboxTactil = false;
  /** @type {{ t: number, x: number, y: number } | null} Primer toque a la espera del segundo. */
  #lightboxUltimoToque = null;
  /** El overlay pide el mismo lock que el menú; el contador vive en `core/scroll-lock`. */
  #lightboxScrollLocked = false;

  /** @type {IntersectionObserver | null} Qué frame se ve en la pista (camino nativo). */
  #frameObserver = null;

  /**
   * Bucle del scroll nativo (`bucleNativo`, `core/embla`): el juego entero de frames
   * clonado a cada lado de la pista, solo cuando la pista es un carril horizontal que
   * desborda. Los clones no son frames (`data-frame-clon`), así que `frames`, el
   * paginador y el lightbox siguen contando las fotos reales; van `inert` y `aria-hidden`.
   * @type {ReturnType<typeof bucleNativo> | null}
   */
  #bucle = null;
  /**
   * Hay un desplazamiento PROGRAMADO en curso (flecha, miniatura): el activo ya es el
   * destino y no se toca mientras la animación pasa por las fotos intermedias. Se
   * libera al asentar.
   * @type {boolean}
   */
  #viajando = false;
  /** @type {ResizeObserver | null} reevalúa el bucle y recoloca cuando la pista cambia de tamaño */
  #roBucle = null;
  /** @type {(() => void) | null} */
  #cancelarGate = null;
  #desconectado = false;
  /**
   * Se renueva en cada conexión y se aborta al desconectar: un custom element puede
   * desconectarse y volver a conectarse —el carrito y la PLP reemplazan fragmentos
   * enteros—, y sin esto cada reconexión sumaba otro listener a los controles y dejaba
   * `#desconectado` en `true`, que impedía volver a montar el carril.
   * @type {AbortController | null}
   */
  #abort = null;

  /** Una vez que el usuario despliega ("Ver más" o miniatura 3+), no re-colapsar. */
  #galleryExpanded = false;
  /** @type {ResizeObserver | null} */
  #infoObserver = null;
  /** @type {(() => void) | null} Recalcula el estado flotante del botón "Ver más". */
  #dockUpdate = null;
  /** @type {(() => void) | null} */
  #dockCleanup = null;

  connectedCallback() {
    this.#desconectado = false;
    this.#abort?.abort();
    this.#abort = new AbortController();

    this.stage = this.querySelector(".pdp__stage");
    this.track = this.querySelector("[data-stage-track]");
    this.thumbsRoot = this.querySelector("[data-thumbs]");
    this.thumbsTrack = this.querySelector("[data-thumbs-track]");
    this.frames = [...this.querySelectorAll("[data-frame]")];
    this.thumbs = [...this.querySelectorAll("[data-thumb]")];
    this.progresoRoot = this.querySelector("[data-stage-progress]");
    this.progreso = [...(this.progresoRoot?.children ?? [])];
    const inicial = this.thumbs.findIndex((t) => t.classList.contains("is-active"));
    this.#indice = inicial < 0 ? 0 : inicial;

    // Las dos pistas desbordan en horizontal, así que las dos se llevaban el gesto
    // vertical de quien pasa el puntero por encima para seguir bajando por la ficha.
    // Es el punto de la PDP donde más se nota: la galería ocupa media pantalla.
    cederEjeVertical(this.track);
    cederEjeVertical(this.thumbsTrack);

    this.#bindStageNav();
    this.#observeFrames();
    this.#marcarVisibles(this.#indice);

    this.addEventListener(
      "click",
      (event) => {
        if (!(event.target instanceof Element)) return;
        const zoom = event.target.closest("[data-frame-link]");
        if (!zoom || !this.contains(zoom)) return;
        // Con JS el ancla no navega: ni pestaña nueva (`target=_blank`) ni la URL cruda.
        event.preventDefault();
        if (arrastrandoCarrusel()) return;
        const links = this.#realFrameLinks();
        const index = links.findIndex((link) => link.contains(zoom) || link === zoom);
        this.#lightboxOpener = zoom;
        void this.#ensureLightbox(index < 0 ? 0 : index);
      },
      { capture: true, signal: this.#abort.signal },
    );
    this.addEventListener(
      "auxclick",
      (event) => {
        if (!(event.target instanceof Element)) return;
        const zoom = event.target.closest("[data-frame-link]");
        if (!zoom || !this.contains(zoom)) return;
        event.preventDefault();
      },
      { capture: true, signal: this.#abort.signal },
    );

    this.addEventListener("click", (event) => {
      if (!(event.target instanceof Element)) return;
      if (event.defaultPrevented) return;

      const more = event.target.closest("[data-gallery-more]");
      if (more) {
        this.#expand();
        const target = this.frames[4] ?? this.frames[this.frames.length - 1];
        if (target) requestAnimationFrame(() => target.scrollIntoView({ behavior: this.#behavior, block: "center" }));
        return;
      }

      const thumb = event.target.closest("[data-thumb]");
      if (thumb) {
        if (this.classList.contains("is-collapsed") && this.thumbs.indexOf(thumb) >= 2) {
          this.#expand();
        }
        this.#goTo(thumb.dataset.imageId);
      }
    });

    // Precarga el lightbox al primer hover/focus para que el click ya abra nativo.
    const preload = () => this.#ensureLightbox();
    this.addEventListener("pointerenter", preload, { once: true });
    this.addEventListener("focusin", preload, { once: true });

    this.closest("product-page")?.addEventListener("variant:changed", (event) => {
      const imageId = event.detail?.featured_image_id;
      if (imageId) this.#goTo(String(imageId));
    });

    this.#buildMode();
    this.#onModeChange = () => this.#buildMode();
    DESKTOP.addEventListener("change", this.#onModeChange);
    this.#bindGalleryDock();

    // La galería está por encima del pliegue en una PDP, así que el observador dispara
    // en la primera entrega; el gate está por coherencia con el resto de los carruseles
    // y para el caso de una PDP embebida más abajo (quick view).
    this.#cancelarGate = alVer(this, () => this.#activarCarril());
  }

  disconnectedCallback() {
    this.#desconectado = true;
    this.#abort?.abort();
    this.#cancelarGate?.();
    if (this.#onModeChange) DESKTOP.removeEventListener("change", this.#onModeChange);
    this.#infoObserver?.disconnect();
    this.#dockCleanup?.();
    this.#frameObserver?.disconnect();
    this.#roBucle?.disconnect();
    this.#roBucle = null;
    this.#quitarClones();
    this.#lightbox?.destroy?.();
    this.#lightbox = null;
  }

  /**
   * Monta el carril nativo: qué foto se ve (`IntersectionObserver`), el bucle de clones si
   * la pista desborda y el estado de las flechas. En la rejilla editorial de escritorio la
   * pista no desborda, así que no hay clones y las flechas no tienen nada que hacer.
   */
  #activarCarril() {
    if (this.#desconectado || !this.track || !this.stage || this.frames.length < 2) return;

    this.#observeFrames();
    this.#sincronizarBucle();
    this.#syncStageArrows();

    // La pista puede medir 0 al llegar aquí (galería en un panel aún oculto) o cambiar
    // de tamaño después: entonces se decide otra vez si hay bucle y se vuelve a la foto
    // en la que se estaba.
    if (!this.#roBucle && "ResizeObserver" in window && this.track) {
      this.#roBucle = new ResizeObserver(() => {
        this.#sincronizarBucle();
        this.#recolocarBucle();
      });
      this.#roBucle.observe(this.track);
    }
  }

  /** Deja la pista sobre la foto `#indice` (idempotente; tras un cambio de tamaño). */
  #recolocarBucle() {
    if (!this.#bucle?.activo()) return;
    this.#bucle.colocar(this.#indice, "instant", true);
  }

  /** La pista desborda en horizontal: es un carril (móvil, o `carousel`/`duo` en escritorio). */
  #pistaDesplaza() {
    const track = this.track;
    if (!track || this.frames.length < 2) return false;
    const primero = this.frames[0];
    const ultimo = this.frames[this.frames.length - 1];
    return ultimo.offsetLeft + ultimo.offsetWidth - primero.offsetLeft > track.clientWidth + 1;
  }

  /** @param {HTMLElement} el posición de scroll que deja `el` al borde de la pista */
  #posicion(el) {
    return el.offsetLeft - (this.track?.offsetLeft ?? 0);
  }

  /**
   * Pone o quita el bucle según la pista sea hoy un carril nativo que desborda. Se
   * reevalúa con el marcador (ancho y puntero) y con el tamaño: en la rejilla editorial
   * no hay clones.
   */
  #sincronizarBucle() {
    const track = this.track;
    if (!track) return;
    if (!this.#bucle) {
      this.#bucle = bucleNativo({
        viewport: track,
        pista: track,
        slides: () => this.frames,
        marcarClon: (clon, vecino) => {
          clon.removeAttribute("data-frame");
          clon.removeAttribute("data-image-id");
          clon.setAttribute("data-frame-clon", "");
          clon.setAttribute("aria-hidden", "true");
          clon.setAttribute("inert", "");
          clon.classList.remove("is-visible");
          clon.querySelectorAll("[data-frame-link]").forEach((a) => a.removeAttribute("data-frame-link"));
          clon.querySelectorAll("[id]").forEach((el) => el.removeAttribute("id"));
          if (vecino) {
            clon.querySelectorAll("img").forEach((img) => {
              img.loading = "eager";
            });
          }
        },
        alAsentar: () => {
          this.#viajando = false;
          this.#syncStageArrows();
        },
        signal: this.#abort?.signal,
      });
      // EL PAGINADOR Y LAS MINIATURAS SIGUEN AL DEDO TAMBIÉN SOBRE LOS CLONES. El
      // `IntersectionObserver` de `#observeFrames` solo mira las fotos reales, así que
      // en la zona de clones —hasta recolocar— se quedaba en la anterior. Aquí el activo
      // se lee de la posición en cada `scroll`: un clon cuenta como su original. Con un
      // viaje programado (flecha, miniatura) no: el activo ya es el destino.
      this.track.addEventListener(
        "scroll",
        () => {
          if (this.#viajando || !this.#bucle?.activo()) return;
          const i = this.#bucle.indice();
          if (i === this.#indice) return;
          const frame = this.frames[i];
          if (!frame) return;
          this.#setActive(frame.dataset.imageId);
          this.#scrollThumbIntoView(i);
        },
        { passive: true, signal: this.#abort?.signal },
      );
    }
    if (!this.#pistaDesplaza()) {
      this.#bucle.quitar();
      return;
    }
    if (this.#bucle.activo()) return;
    // La foto que toca, en seco: el juego delantero de clones no debe verse al colocar.
    if (this.#bucle.poner()) this.#bucle.colocar(this.#indice, "instant", true);
  }

  #quitarClones() {
    this.#bucle?.quitar();
  }



  get #esDuo() {
    return this.classList.contains("pdp__gallery--duo") && DESKTOP.matches;
  }

  /**
   * Qué fotos cuentan como «a la vista». En `duo` son DOS consecutivas (la
   * seleccionada y la de al lado, con wrap); en el resto, solo la actual.
   * @param {number} inicio
   * @returns {number[]}
   */
  #indicesALaVista(inicio) {
    const n = this.frames.length;
    if (!n) return [];
    const i = ((inicio % n) + n) % n;
    if (this.#esDuo && n > 1) return [i, (i + 1) % n];
    return [i];
  }

  /** Índice de la foto que abre la ventana visible. No se lee del DOM: con wrap
   *  las miniaturas 0 y n-1 quedan las dos encendidas y `findIndex` devolvería 0. */
  #indice = 0;

  /** @param {number} inicio */
  #marcarVisibles(inicio) {
    const n = this.frames.length;
    this.#indice = n ? ((inicio % n) + n) % n : 0;
    const vistos = new Set(this.#indicesALaVista(this.#indice));
    this.frames.forEach((f, j) => f.classList.toggle("is-visible", vistos.has(j)));
    this.thumbs.forEach((thumb) => {
      const i = this.frames.findIndex((f) => f.dataset.imageId === thumb.dataset.imageId);
      const on = vistos.has(i);
      thumb.classList.toggle("is-active", on);
      if (on) thumb.setAttribute("aria-current", "true");
      else thumb.removeAttribute("aria-current");
    });
    this.progresoRoot?.style.setProperty("--pdp-progress-i", String(this.#indice));
    this.progreso?.forEach((seg, j) => seg.classList.toggle("is-active", j === this.#indice));
  }

  #realFrameLinks() {
    return /** @type {HTMLAnchorElement[]} */ ([...this.querySelectorAll("[data-frame-link]")]);
  }

  /** @returns {{ src: string, alt: string, thumbSrc?: string, thumbLabel?: string }[]} */
  #lightboxItems() {
    return this.#realFrameLinks().map((a) => ({
      src: a.href,
      alt: a.querySelector("img")?.alt ?? "",
      thumbSrc: a.dataset.thumbSrc || a.querySelector("img")?.currentSrc || a.href,
      thumbLabel: a.dataset.thumbLabel || "",
    }));
  }

  /**
   * Overlay propio: riel Embla para cambiar de foto, zoom CSS en la imagen.
   * PhotoSwipe se reserva para pinch si hiciera falta; el swipe, las flechas y
   * las miniaturas no pasan por él.
   * @param {number | null} [openIndex]
   */
  async #ensureLightbox(openIndex = null) {
    if (this.#lightbox) {
      if (openIndex != null) this.#lightbox.loadAndOpen(openIndex);
      return;
    }
    if (this.#lightboxLoading) {
      if (openIndex != null) this.#pendingOpen = openIndex;
      return;
    }
    if (!this.querySelector("[data-frame-link]")) return;

    this.#lightboxLoading = true;
    try {
      const EmblaCarousel = await cargarEmbla();
      if (this.#desconectado) return;
      this.#lightbox = {
        loadAndOpen: (index) => this.#openLightbox(EmblaCarousel, index),
        destroy: () => this.#teardownLightbox(),
      };
    } finally {
      this.#lightboxLoading = false;
    }

    const idx = openIndex != null ? openIndex : this.#pendingOpen;
    this.#pendingOpen = null;
    if (idx != null) this.#lightbox?.loadAndOpen(idx);
  }

  /**
   * @param {any} EmblaCarousel
   * @param {number} index
   */
  #openLightbox(EmblaCarousel, index) {
    const items = this.#lightboxItems();
    if (!items.length) return;

    const root = this.#lightboxRoot ?? this.#buildLightboxShell();
    this.#lightboxRoot = root;
    const start = Math.min(Math.max(0, index), items.length - 1);

    if (root.classList.contains("is-open") && this.#lightboxEmbla) {
      this.#lightboxZoom = 1;
      this.#lightboxPan = { x: 0, y: 0 };
      this.#lightboxEmbla.scrollTo(start, instante());
      this.#applyLightboxZoom();
      this.#syncLightboxThumbs(start);
      return;
    }

    this.#lightboxEmbla?.destroy();
    this.#lightboxEmbla = null;
    this.#fillLightboxSlides(root, items);

    const viewport = root.querySelector("[data-lightbox-viewport]");
    const track = root.querySelector("[data-lightbox-track]");
    if (!(viewport instanceof HTMLElement) || !(track instanceof HTMLElement)) return;

    this.#lightboxZoom = 1;
    this.#lightboxPan = { x: 0, y: 0 };
    this.#applyLightboxZoom();

    this.#lightboxEmbla = montar(EmblaCarousel, viewport, {
      host: root,
      container: track,
      slides: "[data-lightbox-slide]",
      wrapSiguiente: true,
      rueda: false,
      aislar: true,
      teclado: viewport,
      opciones: {
        align: "center",
        loop: items.length > 1,
        containScroll: items.length > 1 ? false : "trimSnaps",
        startIndex: start,
        // Con la foto ampliada el arrastre es para MOVERLA, no para cambiar de foto:
        // el riel suelta el gesto en cuanto hay zoom.
        watchDrag: () => this.#lightboxZoom <= ZOOM_PAN,
      },
    });
    this.#lightboxEmbla.on("select", () => {
      this.#lightboxZoom = 1;
      this.#lightboxPan = { x: 0, y: 0 };
      this.#lightboxUltimoToque = null;
      root.classList.remove("is-tracking", "is-gesto");
      this.#applyLightboxZoom();
      this.#syncLightboxThumbs(this.#lightboxEmbla.selectedScrollSnap());
    });

    const arrows = root.querySelector(".pdp-lightbox__arrows");
    if (arrows instanceof HTMLElement) arrows.hidden = items.length < 2;
    const thumbs = root.querySelector("[data-lightbox-thumbs]");
    if (thumbs instanceof HTMLElement) thumbs.hidden = items.length < 2;

    this.#fillLightboxThumbs(root, items, start);
    this.#syncLightboxThumbs(start);

    root.hidden = false;
    root.classList.add("is-open");
    document.body.classList.add("pdp-lightbox-open");
    this.#lockLightboxScroll();
    viewport.focus({ preventScroll: true });
    this.#bindLightboxKeys();
  }

  #buildLightboxShell() {
    const root = document.createElement("div");
    root.className = "pdp-lightbox";
    root.setAttribute("role", "dialog");
    root.setAttribute("aria-modal", "true");
    if (this.dataset.lightboxThumbs) root.setAttribute("aria-label", this.dataset.lightboxThumbs);
    root.hidden = true;
    root.innerHTML = `
      <div class="pdp-lightbox__thumbs" data-lightbox-thumbs hidden></div>
      <div class="pdp-lightbox__bar">
        <div class="pdp-lightbox__wish" data-lightbox-wish></div>
        <button type="button" class="pdp-lightbox__close" data-lightbox-close aria-label="${this.dataset.lightboxClose || ""}">${LIGHTBOX_CLOSE}</button>
      </div>
      <div class="pdp-lightbox__stage">
        <div class="pdp-lightbox__viewport" data-lightbox-viewport tabindex="0"></div>
        <div class="pdp-lightbox__arrows">
          <button type="button" class="pdp-lightbox__arrow pdp-lightbox__arrow--prev" data-lightbox-prev aria-label="${this.dataset.lightboxPrev || ""}">${LIGHTBOX_ARROW_PREV}</button>
          <button type="button" class="pdp-lightbox__arrow pdp-lightbox__arrow--next" data-lightbox-next aria-label="${this.dataset.lightboxNext || ""}">${LIGHTBOX_ARROW_NEXT}</button>
        </div>
      </div>
      <div class="pdp-lightbox__zoom" data-lightbox-zoom role="group"></div>
    `;

    const wishHost = root.querySelector("[data-lightbox-wish]");
    const tpl = this.querySelector("[data-lightbox-wishlist]");
    if (wishHost instanceof HTMLElement && tpl instanceof HTMLTemplateElement && tpl.content.firstElementChild) {
      wishHost.append(tpl.content.firstElementChild.cloneNode(true));
      void import("core/wishlist").then((mod) => mod.syncWishlistUi(wishHost));
    } else if (wishHost instanceof HTMLElement) {
      wishHost.hidden = true;
    }

    const zoom = root.querySelector("[data-lightbox-zoom]");
    if (zoom instanceof HTMLElement) this.#initLightboxZoom(zoom);

    const pozo = root.querySelector(".pdp-lightbox__stage");
    if (pozo instanceof HTMLElement) this.#bindLightboxTouch(pozo, root);

    root.querySelector("[data-lightbox-close]")?.addEventListener("click", () => this.#closeLightbox());
    root.querySelector("[data-lightbox-prev]")?.addEventListener("click", () => this.#lightboxStep(-1));
    root.querySelector("[data-lightbox-next]")?.addEventListener("click", () => this.#lightboxStep(1));
    root.addEventListener("click", (event) => {
      if (!(event.target instanceof Element)) return;
      if (event.target.closest(".pdp-lightbox__arrow, [data-lightbox-zoom], [data-lightbox-thumbs], .pdp-lightbox__bar")) return;
      const img = event.target.closest("[data-lightbox-slide] img");
      if (!img || !root.contains(img)) return;
      event.preventDefault();
      if (arrastrandoCarrusel()) return;
      // Un toque suelto NO amplía: en una pantalla táctil el dedo roza la foto todo
      // el rato (al cerrar, al pasar de foto) y saltar a 200% en cada roce es el
      // ruido que hacía el zoom impredecible. Con el dedo amplía el DOBLE toque.
      if (this.#lightboxTactil) return;
      this.#rememberLightboxPointer(event);
      this.#nudgeLightboxZoom(1);
    });

    root.addEventListener(
      "pointermove",
      (event) => {
        if (!(event instanceof PointerEvent)) return;
        // SOLO EL RATÓN. «La foto va a donde apuntas» es un gesto de puntero fino:
        // con el dedo el mismo cálculo movía la foto al REVÉS y a un tercio de la
        // distancia (medido: 80px de dedo → −27,7px de foto). El dedo la arrastra,
        // y eso lo lleva `#bindLightboxTouch`.
        if (event.pointerType !== "mouse") return;
        this.#lightboxTactil = false;
        if (!root.classList.contains("is-panning")) return;
        // El desplazamiento sigue al ratón en directo: `is-tracking` apaga su
        // transición mientras dura (el CSS la mantiene para el escalado).
        root.classList.add("is-tracking");
        this.#rememberLightboxPointer(event);
        this.#panLightboxFromPointer();
      },
      { capture: true, passive: true },
    );

    document.body.append(root);
    return root;
  }

  /**
   * @param {HTMLElement} root
   * @param {{ src: string, alt: string }[]} items
   */
  #fillLightboxSlides(root, items) {
    const viewport = root.querySelector("[data-lightbox-viewport]");
    if (!(viewport instanceof HTMLElement)) return;
    const track = document.createElement("div");
    track.className = "pdp-lightbox__track";
    track.setAttribute("data-lightbox-track", "");
    items.forEach((item) => {
      const slide = document.createElement("figure");
      slide.className = "pdp-lightbox__slide";
      slide.setAttribute("data-lightbox-slide", "");
      const img = document.createElement("img");
      img.src = item.src;
      img.alt = item.alt;
      img.draggable = false;
      slide.append(img);
      track.append(slide);
    });
    viewport.replaceChildren(track);
  }

  /**
   * @param {HTMLElement} root
   * @param {{ thumbSrc?: string, thumbLabel?: string, alt?: string }[]} items
   * @param {number} current
   */
  #fillLightboxThumbs(root, items, current) {
    const el = root.querySelector("[data-lightbox-thumbs]");
    if (!(el instanceof HTMLElement)) return;
    el.replaceChildren();
    if (this.dataset.lightboxThumbs) el.setAttribute("aria-label", this.dataset.lightboxThumbs);
    items.forEach((item, i) => {
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = "pdp__thumb pdp-lightbox__thumb";
      btn.setAttribute("aria-label", item.thumbLabel || item.alt || String(i + 1));
      const img = document.createElement("img");
      img.src = item.thumbSrc || "";
      img.alt = "";
      btn.append(img);
      btn.addEventListener("click", (event) => {
        event.preventDefault();
        this.#lightboxEmbla?.scrollTo(i, instante());
        this.#syncLightboxThumbs(i);
      });
      el.append(btn);
    });
    this.#syncLightboxThumbs(current);
  }

  /** @param {number} current */
  #syncLightboxThumbs(current) {
    const el = this.#lightboxRoot?.querySelector("[data-lightbox-thumbs]");
    if (!(el instanceof HTMLElement)) return;
    [...el.children].forEach((child, j) => {
      if (!(child instanceof HTMLElement)) return;
      const on = j === current;
      child.classList.toggle("is-active", on);
      child.setAttribute("aria-current", on ? "true" : "false");
    });
    const active = el.children[current];
    if (active instanceof HTMLElement) {
      active.scrollIntoView({ inline: "nearest", block: "nearest", behavior: prefersReduced() ? "auto" : "smooth" });
    }
  }

  /** @param {number} dir */
  #lightboxStep(dir) {
    const api = this.#lightboxEmbla;
    if (!api) return;
    const jump = instante();
    if (dir > 0) {
      if (api.canScrollNext()) api.scrollNext(jump);
      else api.scrollTo(0, true);
    } else if (api.canScrollPrev()) {
      api.scrollPrev(jump);
    } else {
      api.scrollTo(api.scrollSnapList().length - 1, true);
    }
  }

  /** @param {HTMLElement} el */
  #initLightboxZoom(el) {
    if (this.dataset.lightboxZoom) el.setAttribute("aria-label", this.dataset.lightboxZoom);
    const menos = document.createElement("button");
    menos.type = "button";
    menos.className = "pdp-lightbox__zoom-btn";
    menos.setAttribute("aria-label", this.dataset.lightboxZoomOut || "−");
    menos.textContent = "−";
    const valor = document.createElement("span");
    valor.className = "pdp-lightbox__zoom-value";
    valor.setAttribute("aria-live", "polite");
    const mas = document.createElement("button");
    mas.type = "button";
    mas.className = "pdp-lightbox__zoom-btn";
    mas.setAttribute("aria-label", this.dataset.lightboxZoomIn || "+");
    mas.textContent = "+";
    el.append(menos, valor, mas);
    menos.addEventListener("click", (event) => {
      event.preventDefault();
      this.#nudgeLightboxZoom(-1);
    });
    mas.addEventListener("click", (event) => {
      event.preventDefault();
      this.#nudgeLightboxZoom(1);
    });
    this.#lightboxZoomValue = valor;
    this.#lightboxZoomOut = menos;
    this.#lightboxZoomIn = mas;
    this.#applyLightboxZoom();
  }

  /**
   * GESTOS CON EL DEDO SOBRE LA FOTO AMPLIADA.
   *
   * Tres, los que ya sabe cualquiera que haya mirado una foto en un móvil:
   *
   *  · PELLIZCAR — zoom continuo, anclado a los dedos: el punto que pinzas se queda
   *    donde está en vez de irse al centro. El punto medio también arrastra, así que
   *    ampliar y recolocar es UN solo gesto y no dos.
   *  · ARRASTRAR — con la foto ampliada, la foto va con el dedo 1:1. Antes iba al
   *    revés y a un tercio, porque se le aplicaba el «mira a donde apuntas» del ratón.
   *  · DOBLE TOQUE — alterna 100% ↔ 200% sobre el punto tocado. Es el atajo, y sobre
   *    todo es la SALIDA: volver a 100% era pulsar «−» tres veces en un botón de 15px.
   *
   * Un toque suelto no hace nada (lo ignora el manejador de clic del overlay).
   * Mientras dura el gesto, `is-gesto` apaga la transición de la foto: una foto que
   * llega 380ms tarde a donde están los dedos no se lee como zoom, se lee como retardo.
   *
   * El ratón no entra aquí: conserva su clic para ampliar y su paneo por cursor.
   *
   * @param {HTMLElement} pozo
   * @param {HTMLElement} root
   */
  #bindLightboxTouch(pozo, root) {
    /** @type {Map<number, { x: number, y: number }>} */
    const dedos = new Map();
    /** @type {{ dist: number, zoom: number, medio: { x: number, y: number } } | null} */
    let pinza = null;
    let arrastrando = false;
    /** @type {{ t: number, x: number, y: number, movido: boolean } | null} */
    let toque = null;

    /** @param {PointerEvent} e */
    const punto = (e) => ({ x: e.clientX, y: e.clientY });
    const pares = () => /** @type {{ x: number, y: number }[]} */ ([...dedos.values()]);
    const medio = () => {
      const [a, b] = pares();
      return { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
    };
    const separacion = () => {
      const [a, b] = pares();
      return Math.hypot(a.x - b.x, a.y - b.y);
    };

    pozo.addEventListener(
      "pointerdown",
      (event) => {
        if (!(event instanceof PointerEvent) || event.pointerType === "mouse") return;
        this.#lightboxTactil = true;
        dedos.set(event.pointerId, punto(event));
        root.classList.add("is-gesto");
        if (dedos.size === 2) {
          pinza = { dist: separacion(), zoom: this.#lightboxZoom, medio: medio() };
          arrastrando = false;
          toque = null;
          return;
        }
        if (dedos.size === 1) {
          arrastrando = this.#lightboxZoom > ZOOM_PAN;
          toque = { t: performance.now(), ...punto(event), movido: false };
        }
      },
      { passive: true },
    );

    pozo.addEventListener(
      "pointermove",
      (event) => {
        if (!(event instanceof PointerEvent) || event.pointerType === "mouse") return;
        const previo = dedos.get(event.pointerId);
        if (!previo) return;
        dedos.set(event.pointerId, punto(event));
        if (toque && Math.hypot(event.clientX - toque.x, event.clientY - toque.y) > TOQUE_PX) {
          toque.movido = true;
        }

        if (dedos.size >= 2 && pinza && pinza.dist > 0) {
          const centro = medio();
          // Los dos dedos moviéndose a la vez son un arrastre; la razón entre
          // separaciones es el zoom. Las dos cosas, en el mismo gesto.
          this.#lightboxPan.x += centro.x - pinza.medio.x;
          this.#lightboxPan.y += centro.y - pinza.medio.y;
          pinza.medio = centro;
          this.#zoomLightboxA((separacion() / pinza.dist) * pinza.zoom, centro);
          return;
        }

        if (arrastrando && this.#lightboxZoom > ZOOM_PAN) {
          this.#lightboxPan.x += event.clientX - previo.x;
          this.#lightboxPan.y += event.clientY - previo.y;
          this.#clampLightboxPan();
          this.#aplicarLightboxPan();
        }
      },
      { passive: true },
    );

    /** @param {PointerEvent} event */
    const soltar = (event) => {
      if (!(event instanceof PointerEvent) || event.pointerType === "mouse") return;
      dedos.delete(event.pointerId);
      if (dedos.size < 2) pinza = null;
      if (dedos.size === 1) {
        // Levantar UN dedo de la pinza deja el otro paneando desde donde está,
        // sin el salto que daría seguir contra la posición vieja.
        arrastrando = this.#lightboxZoom > ZOOM_PAN;
        return;
      }
      if (dedos.size > 0) return;

      root.classList.remove("is-gesto");
      arrastrando = false;
      const fin = toque;
      toque = null;
      if (!fin || fin.movido || performance.now() - fin.t > TOQUE_MS) {
        this.#lightboxUltimoToque = null;
        return;
      }

      const previo = this.#lightboxUltimoToque;
      const ahora = performance.now();
      if (previo && ahora - previo.t < DOBLE_MS && Math.hypot(fin.x - previo.x, fin.y - previo.y) < DOBLE_PX) {
        this.#lightboxUltimoToque = null;
        this.#alternarZoomTactil({ x: fin.x, y: fin.y });
        return;
      }
      this.#lightboxUltimoToque = { t: ahora, x: fin.x, y: fin.y };
    };

    pozo.addEventListener("pointerup", soltar, { passive: true });
    pozo.addEventListener("pointercancel", soltar, { passive: true });
  }

  /**
   * Doble toque: si hay zoom, se sale del todo; si no, se entra al punto tocado.
   * @param {{ x: number, y: number }} ancla
   */
  #alternarZoomTactil(ancla) {
    if (this.#lightboxZoom > ZOOM_PAN) {
      this.#lightboxPan = { x: 0, y: 0 };
      this.#zoomLightboxA(1, null);
      return;
    }
    this.#zoomLightboxA(ZOOM_DOBLE, ancla);
  }

  /**
   * Fija el zoom manteniendo quieto el punto `ancla` de la pantalla. Sin esto, ampliar
   * siempre tira del encuadre hacia el centro y hay que recolocar detrás de cada paso.
   *
   * @param {number} z
   * @param {{ x: number, y: number } | null} ancla
   */
  #zoomLightboxA(z, ancla) {
    const previo = this.#lightboxZoom;
    const nuevo = Math.min(ZOOM_MAX, Math.max(1, z));
    const pozo = this.#lightboxRoot?.querySelector(".pdp-lightbox__stage");
    if (ancla && previo > 0 && pozo instanceof HTMLElement) {
      const caja = pozo.getBoundingClientRect();
      const cx = caja.left + caja.width / 2;
      const cy = caja.top + caja.height / 2;
      const factor = nuevo / previo;
      this.#lightboxPan.x = ancla.x - cx - (ancla.x - cx - this.#lightboxPan.x) * factor;
      this.#lightboxPan.y = ancla.y - cy - (ancla.y - cy - this.#lightboxPan.y) * factor;
    }
    this.#lightboxZoom = Math.round(nuevo * 1000) / 1000;
    // Un salto pedido (doble toque, `0`) se suaviza. Durante la pinza manda
    // `is-gesto`, que va después en la hoja y apaga la transición igualmente.
    this.#lightboxRoot?.classList.remove("is-tracking");
    this.#applyLightboxZoom();
  }

  /** Cuánto se puede desplazar la foto en cada eje sin dejar hueco dentro del pozo. */
  #lightboxPanMax() {
    const img = this.#lightboxActiveImg();
    const pozo = this.#lightboxRoot?.querySelector(".pdp-lightbox__stage");
    if (!img || !(pozo instanceof HTMLElement)) return { x: 0, y: 0 };
    const caja = pozo.getBoundingClientRect();
    const z = this.#lightboxZoom;
    return {
      x: Math.max(0, (img.offsetWidth * z - caja.width) / 2),
      y: Math.max(0, (img.offsetHeight * z - caja.height) / 2),
    };
  }

  #clampLightboxPan() {
    const max = this.#lightboxPanMax();
    this.#lightboxPan.x = Math.min(max.x, Math.max(-max.x, this.#lightboxPan.x));
    this.#lightboxPan.y = Math.min(max.y, Math.max(-max.y, this.#lightboxPan.y));
  }

  #aplicarLightboxPan() {
    const img = this.#lightboxActiveImg();
    if (!img) return;
    img.style.setProperty("--pdp-pan-x", `${this.#lightboxPan.x}px`);
    img.style.setProperty("--pdp-pan-y", `${this.#lightboxPan.y}px`);
  }

  /** @param {number} dir */
  #nudgeLightboxZoom(dir) {
    // Un paso de zoom reencuadra: aquí el translate SÍ transiciona con el escalado.
    this.#lightboxRoot?.classList.remove("is-tracking");
    const next = Math.min(ZOOM_MAX, Math.max(1, this.#lightboxZoom + dir * ZOOM_STEP));
    this.#lightboxZoom = Math.round(next * 100) / 100;
    this.#applyLightboxZoom();
  }

  #applyLightboxZoom() {
    const root = this.#lightboxRoot;
    if (!root) return;
    const i = this.#lightboxEmbla?.selectedScrollSnap() ?? 0;
    root.querySelectorAll("[data-lightbox-slide] img").forEach((img, j) => {
      if (!(img instanceof HTMLElement)) return;
      img.style.setProperty("--pdp-zoom", j === i ? String(this.#lightboxZoom) : "1");
      if (j !== i) {
        img.style.setProperty("--pdp-pan-x", "0px");
        img.style.setProperty("--pdp-pan-y", "0px");
      }
    });
    if (this.#lightboxZoomValue) {
      // Con la pinza el valor cambia en cada fotograma: anunciarlos todos convierte
      // el lector de pantalla en ruido. Se calla durante el gesto y vuelve a hablar
      // al soltar, para los cambios que sí son un salto (botones, doble toque, teclas).
      this.#lightboxZoomValue.setAttribute("aria-live", root.classList.contains("is-gesto") ? "off" : "polite");
      this.#lightboxZoomValue.textContent = `${Math.round(this.#lightboxZoom * 100)}%`;
    }
    if (this.#lightboxZoomOut) this.#lightboxZoomOut.disabled = this.#lightboxZoom <= 1.001;
    if (this.#lightboxZoomIn) this.#lightboxZoomIn.disabled = this.#lightboxZoom >= ZOOM_MAX - 0.001;
    root.classList.toggle("is-zoomed", this.#lightboxZoom > 1.001);
    root.classList.toggle("is-panning", this.#lightboxZoom > ZOOM_PAN);
    root.classList.toggle("is-zoomed-max", this.#lightboxZoom >= ZOOM_MAX - 0.001);
    if (this.#lightboxTactil) {
      // El dedo ya decidió el encuadre; aquí solo se recorta a lo que cabe (al
      // alejar, la foto vuelve dentro del pozo sin dejar franjas vacías).
      this.#clampLightboxPan();
      this.#aplicarLightboxPan();
      return;
    }
    this.#panLightboxFromPointer();
  }

  /** @param {PointerEvent | MouseEvent} event */
  #rememberLightboxPointer(event) {
    this.#lightboxPointer = { x: event.clientX, y: event.clientY };
  }

  /**
   * Follow-cursor: el puntero en el borde del pozo alinea ese extremo de la
   * foto (mango arriba, base abajo). El translate va en px de pantalla, después
   * del scale, y se clamp a lo que cabe en el pozo — no al centro recortado.
   */
  #panLightboxFromPointer() {
    const img = this.#lightboxActiveImg();
    if (!img) return;
    if (this.#lightboxZoom <= ZOOM_PAN) {
      this.#lightboxPan = { x: 0, y: 0 };
      this.#aplicarLightboxPan();
      return;
    }
    const well = this.#lightboxRoot?.querySelector(".pdp-lightbox__stage");
    if (!(well instanceof HTMLElement)) return;
    const box = well.getBoundingClientRect();
    if (box.width < 1 || box.height < 1) return;
    const z = this.#lightboxZoom;
    const maxX = Math.max(0, (img.offsetWidth * z - box.width) / 2);
    const maxY = Math.max(0, (img.offsetHeight * z - box.height) / 2);
    const pt = this.#lightboxPointer;
    const nx = pt ? Math.min(1, Math.max(0, (pt.x - box.left) / box.width)) : 0.5;
    const ny = pt ? Math.min(1, Math.max(0, (pt.y - box.top) / box.height)) : 0.5;
    this.#lightboxPan = { x: maxX * (1 - 2 * nx), y: maxY * (1 - 2 * ny) };
    this.#aplicarLightboxPan();
  }

  /** @returns {HTMLElement | null} */
  #lightboxActiveImg() {
    const i = this.#lightboxEmbla?.selectedScrollSnap() ?? 0;
    const img = this.#lightboxRoot?.querySelectorAll("[data-lightbox-slide] img")[i];
    return img instanceof HTMLElement ? img : null;
  }

  #bindLightboxKeys() {
    this.#unbindLightboxKeys();
    /** @param {KeyboardEvent} event */
    const onKey = (event) => {
      if (!this.#lightboxRoot?.classList.contains("is-open")) return;
      if (event.key === "Escape") {
        event.preventDefault();
        this.#closeLightbox();
        return;
      }
      if (event.key === "ArrowRight") {
        event.preventDefault();
        this.#lightboxStep(1);
      } else if (event.key === "ArrowLeft") {
        event.preventDefault();
        this.#lightboxStep(-1);
      } else if (event.key === "+" || event.key === "=") {
        // Sin ratón el zoom vivía solo en dos botones a los que había que llegar
        // tabulando. `+`, `-` y `0` son los mismos atajos que el zoom del navegador,
        // y el `0` es la salida rápida a 100%.
        event.preventDefault();
        this.#nudgeLightboxZoom(1);
      } else if (event.key === "-" || event.key === "_") {
        event.preventDefault();
        this.#nudgeLightboxZoom(-1);
      } else if (event.key === "0") {
        event.preventDefault();
        this.#lightboxPan = { x: 0, y: 0 };
        this.#zoomLightboxA(1, null);
      }
    };
    this.#lightboxOnKey = onKey;
    document.addEventListener("keydown", onKey, true);
  }

  #unbindLightboxKeys() {
    if (this.#lightboxOnKey) document.removeEventListener("keydown", this.#lightboxOnKey, true);
    this.#lightboxOnKey = null;
  }

  #lockLightboxScroll() {
    if (this.#lightboxScrollLocked) return;
    lockScroll();
    this.#lightboxScrollLocked = true;
  }

  #unlockLightboxScroll() {
    if (!this.#lightboxScrollLocked) return;
    unlockScroll();
    this.#lightboxScrollLocked = false;
  }

  #closeLightbox() {
    this.#unbindLightboxKeys();
    this.#lightboxEmbla?.destroy();
    this.#lightboxEmbla = null;
    this.#lightboxZoom = 1;
    this.#lightboxPan = { x: 0, y: 0 };
    this.#lightboxPointer = null;
    this.#lightboxUltimoToque = null;
    this.#lightboxRoot?.classList.remove("is-gesto", "is-tracking");
    if (this.#lightboxRoot) {
      this.#lightboxRoot.classList.remove("is-open");
      this.#lightboxRoot.hidden = true;
    }
    document.body.classList.remove("pdp-lightbox-open");
    this.#unlockLightboxScroll();
    const opener = this.#lightboxOpener;
    this.#lightboxOpener = null;
    if (opener instanceof HTMLElement) opener.focus({ preventScroll: true });
  }

  #teardownLightbox() {
    this.#closeLightbox();
    this.#lightboxRoot?.remove();
    this.#lightboxRoot = null;
  }

  get #behavior() {
    return prefersReduced() ? "auto" : "smooth";
  }

  /** @param {string | undefined} imageId */
  #frameFor(imageId) {
    return this.frames.find((frame) => frame.dataset.imageId === String(imageId));
  }

  /**
   * Lleva la galería a la imagen: scroll de la página hasta su frame en desktop,
   * scroll de la pista en mobile.
   * @param {string | undefined} imageId
   */
  #goTo(imageId) {
    const frame = this.#frameFor(imageId);
    if (!frame) return;

    this.#setActive(frame.dataset.imageId);

    // La decisión es del LAYOUT, no del ancho de pantalla: se pregunta si la pista
    // desborda en horizontal. Antes bifurcaba por `DESKTOP.matches`, y eso daba por
    // hecho que en escritorio la galería siempre es la rejilla editorial vertical —
    // donde «ir a la imagen» sí es desplazar la página. Pero esta tienda usa el modo
    // CARRUSEL (`.pdp__gallery--carousel`), que en escritorio pone los frames en una
    // pista horizontal igual que en móvil: allí `scrollIntoView` no tenía nada que
    // desplazar y las flechas no movían nada. Medido: scrollLeft 0 → 0 al pulsar
    // «siguiente» a 1440, mientras a 390 iba de 0 a 366.
    //
    // Preguntando por el desbordamiento sirve para los dos modos y para el que venga,
    // sin que el JS tenga que conocer los nombres de clase del CSS.
    if (this.#pistaDesplaza()) {
      // Desplazar la PISTA, no la página: `scrollIntoView` movería el documento
      // entero además del carrusel. Con bucle, por la copia más cercana (del último al
      // primero es un paso corto por el clon vecino; al asentar se recoloca).
      if (this.#bucle?.activo()) {
        this.#viajando = true;
        this.#bucle.colocar(this.frames.indexOf(frame), this.#behavior);
      } else {
        this.track.scrollTo({ left: this.#posicion(frame), behavior: this.#behavior });
      }
    } else if (DESKTOP.matches) {
      frame.scrollIntoView({ behavior: this.#behavior, block: "center" });
    }
  }

  /**
   * Flechas del stage (solo mobile; en desktop el CSS las oculta). Envuelven en
   * los extremos, que es lo que hacía el `loop: true` de Swiper para el clic de
   * flecha. Envolver no necesita clonar slides: clonar solo hace falta para que
   * el ARRASTRE continúe más allá del último, y eso sí se pierde — a cambio de no
   * duplicar nodos en el árbol de accesibilidad ni en el orden de tabulación.
   */
  #bindStageNav() {
    // PLURAL, y no es defensivo: desde el modo `duo` hay DOS parejas de flechas en el
    // markup —la superpuesta al centro del frame y la del carril de miniaturas— y el CSS
    // decide cuál se ve en cada modo. Con `querySelector` solo la primera del documento
    // quedaba enganchada, así que las flechas del carril no hacían nada y las del stage
    // se quedaban con el `aria-disabled` viejo. Es el mismo patrón que `[data-atc]`.
    const prev = [...this.querySelectorAll("[data-gallery-prev]")];
    const next = [...this.querySelectorAll("[data-gallery-next]")];
    if (!prev.length && !next.length) return;

    /** @param {number} dir */
    const step = (dir) => {
      const n = this.frames.length;
      if (!n) return;
      const siguiente = (this.#visibleFrameIndex() + dir + n) % n;
      this.#goTo(this.frames[siguiente]?.dataset.imageId);
    };

    prev.forEach((boton) => boton.addEventListener("click", () => step(-1)));
    next.forEach((boton) => boton.addEventListener("click", () => step(1)));

    this.#stageNav = { prev, next };
    this.#syncStageArrows();
    // La pista también se arrastra con el dedo y con la rueda, que no pasan por `step`.
    this.track?.addEventListener("scroll", () => this.#syncStageArrows(), { passive: true });
  }

  /** @type {{ prev: Element[], next: Element[] } | null} */
  #stageNav = null;

  #syncStageArrows() {
    if (!this.#stageNav || !this.track) return;
    const { prev, next } = this.#stageNav;

    // Con más de una foto el carrusel envuelve: las dos flechas se quedan.
    if (this.frames.length > 1) {
      prev.forEach((boton) => boton.setAttribute("aria-disabled", "false"));
      next.forEach((boton) => boton.setAttribute("aria-disabled", "false"));
      return;
    }

    prev.forEach((boton) => boton.setAttribute("aria-disabled", "true"));
    next.forEach((boton) => boton.setAttribute("aria-disabled", "true"));
  }

  /** @returns {number} Índice del frame que abre la ventana visible ahora mismo. */
  #visibleFrameIndex() {
    return this.#indice;
  }

  /**
   * Mobile: la miniatura activa la decide qué frame se VE en la pista, no un
   * contador. Así sigue también al arrastre táctil, que no pasa por `#goTo`. Es
   * lo que hacía el módulo Thumbs de Swiper.
   */
  #observeFrames() {
    if (!this.track || !this.frames.length || !("IntersectionObserver" in window)) return;
    this.#frameObserver?.disconnect();

    this.#frameObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          entry.target.classList.toggle("is-visible", entry.isIntersecting);
        }
        // Solo cuando la pista es un carril: en la rejilla editorial la miniatura
        // activa es la ÚLTIMA CLICADA, porque con todos los frames a la vista un
        // scrollspy sería ambiguo. Se pregunta por el layout y no por el ancho: un
        // tablet táctil ancho en modo `carousel` también es carril.
        if (!this.#pistaDesplaza()) return;
        const i = this.frames.findIndex((f) => f.classList.contains("is-visible"));
        if (i < 0) return;
        this.#setActive(this.frames[i].dataset.imageId);
        this.#scrollThumbIntoView(i);
      },
      { root: this.track, threshold: 0.6 },
    );

    this.frames.forEach((f) => this.#frameObserver?.observe(f));
  }

  /** Mantiene visible la miniatura activa dentro de su fila scrolleable. */
  #scrollThumbIntoView(index) {
    const thumb = this.thumbs[index];
    const root = this.thumbsRoot;
    if (!thumb || !root) return;
    // En escritorio la columna sticky no desborda; el carril `duo` sí (28px × n).
    if (DESKTOP.matches && !this.#esDuo) return;

    const izq = thumb.offsetLeft - root.offsetLeft;
    const der = izq + thumb.offsetWidth;
    if (izq < root.scrollLeft || der > root.scrollLeft + root.clientWidth) {
      root.scrollTo({
        left: izq - root.clientWidth / 2 + thumb.offsetWidth / 2,
        behavior: this.#behavior,
      });
    }
  }

  /** @param {string | undefined} imageId */
  #setActive(imageId) {
    const inicio = this.frames.findIndex((f) => f.dataset.imageId === String(imageId));
    this.#marcarVisibles(inicio < 0 ? 0 : inicio);
  }

  #buildMode() {
    if (DESKTOP.matches) {
      this.#applyCollapse();
    } else {
      // Mobile muestra una foto a la vez (carrusel CSS); el colapso no aplica.
      this.#clearCollapse();
    }
  }

  /* ── Colapsar galería (desktop, "Ver más") ── */

  /** Colapsa la galería a la ALTURA de la columna de info (así ambas terminan
   *  juntas, sin hueco blanco) — solo si la galería es más alta que la info. */
  #applyCollapse() {
    if (this.#galleryExpanded) {
      this.#clearCollapse();
      return;
    }
    // rAF: el grid ya está maquetado (los <img> reservan alto por width/height).
    requestAnimationFrame(() => {
      if (!DESKTOP.matches || this.#galleryExpanded || !this.track) return;
      const h = this.#collapsedHeight();
      // Sin beneficio si no hay filas ocultas bajo el corte (margen para diferencias mínimas).
      if (!h || this.track.scrollHeight <= h + 48) {
        this.#clearCollapse();
        return;
      }
      this.classList.add("is-collapsed");
      this.style.setProperty("--gallery-collapsed-h", `${h}px`);
      this.#observeInfo();
      this.#dockUpdate?.();
    });
  }

  /** @returns {HTMLElement | null} La columna de info del PDP. */
  #infoEl() {
    return this.closest("product-page")?.querySelector(".pdp__info") ?? null;
  }

  #infoHeight() {
    const info = this.#infoEl();
    return info ? Math.round(info.getBoundingClientRect().height) : 0;
  }

  /** Alto del colapso: fondo de la última FILA COMPLETA que cabe dentro del alto
   *  de la info (grid de 2 columnas). Así nunca se asoma una fila parcial y la
   *  galería llega lo más cerca posible del fin de la info. Mínimo 2 filas (3 o
   *  4 fotos) aunque la info sea más baja: 1 sola fila deja demasiado hueco
   *  vacío bajo el botón "ver más". */
  #collapsedHeight() {
    const infoH = this.#infoHeight();
    if (!infoH || !this.track || !this.frames.length) return 0;
    const trackTop = this.track.getBoundingClientRect().top;
    const rowBottom = (i) => {
      const bottomA = this.frames[i].getBoundingClientRect().bottom - trackTop;
      const pair = this.frames[i + 1];
      return Math.round(Math.max(bottomA, pair ? pair.getBoundingClientRect().bottom - trackTop : bottomA));
    };
    const minRows = Math.min(2, Math.ceil(this.frames.length / 2));
    let best = rowBottom((minRows - 1) * 2);
    for (let i = minRows * 2; i < this.frames.length; i += 2) {
      const bottom = rowBottom(i);
      if (bottom <= infoH) best = bottom;
      else break;
    }
    return best;
  }

  #syncCollapsedHeight() {
    const h = this.#collapsedHeight();
    if (h > 0) this.style.setProperty("--gallery-collapsed-h", `${h}px`);
  }

  /** Re-sincroniza el corte si la info cambia de alto (acordeón, variante, resize). */
  #observeInfo() {
    if (this.#infoObserver || !("ResizeObserver" in window)) return;
    const info = this.#infoEl();
    if (!info) return;
    this.#infoObserver = new ResizeObserver(() => {
      if (!this.#galleryExpanded && DESKTOP.matches && this.classList.contains("is-collapsed")) {
        this.#syncCollapsedHeight();
      }
    });
    this.#infoObserver.observe(info);
  }

  #clearCollapse() {
    this.classList.remove("is-collapsed");
    this.style.removeProperty("--gallery-collapsed-h");
    this.#dockUpdate?.();
  }

  /** Despliega toda la galería (permanente hasta recargar). */
  #expand() {
    this.#galleryExpanded = true;
    this.#clearCollapse();
    this.querySelector("[data-gallery-more]")?.setAttribute("aria-expanded", "true");
  }

  /** Botón "Ver más": flota fijo al fondo del viewport y ancla cuando el fondo
   *  de la galería colapsada llega a esa línea (en la transición ambas posiciones
   *  coinciden → sin salto). Solo mientras está colapsada en desktop. */
  #bindGalleryDock() {
    const btn = this.querySelector("[data-gallery-more]");
    if (!btn) return;

    let raf = 0;
    const update = () => {
      raf = 0;
      if (!DESKTOP.matches || !this.classList.contains("is-collapsed")) {
        btn.classList.remove("is-floating");
        return;
      }
      const floating = this.getBoundingClientRect().bottom > window.innerHeight;
      btn.classList.toggle("is-floating", floating);
      if (floating && this.stage) {
        const s = this.stage.getBoundingClientRect();
        btn.style.setProperty("--gallery-more-left", `${Math.round(s.left + s.width / 2)}px`);
      }
    };
    this.#dockUpdate = update;

    const onScroll = () => {
      if (!raf) raf = requestAnimationFrame(update);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll, { passive: true });
    this.#dockCleanup = () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
    update();
  }

}

if (!customElements.get("product-gallery")) {
  customElements.define("product-gallery", ProductGallery);
}

export { ProductGallery };
